import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import { Btn } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const inp = "mono mt-1 w-full rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-sm";

const Field = ({ label, children }) => <label className="block text-xs text-slate-600">{label}{children}</label>;

function useBuyable() {
  const [rows, setRows] = useState(null);
  const [halted, setHalted] = useState(false);
  const load = async (extra) => {
    try {
      const [r, s, one] = await Promise.all([api.get("/live/buyable"), api.get("/settings"), extra ? api.get(`/live/buyable/${encodeURIComponent(extra)}`).catch(() => null) : null]);
      const list = r.data.rows.filter((x) => x.buyable && x.symbol !== extra);
      setRows(one?.data?.buyable ? [one.data, ...list] : list); setHalted(!!s.data.halt_new_orders);
      return one?.data;
    } catch (e) { toast.error(errMsg(e)); setRows([]); return null; }
  };
  return [rows, load, halted, setRows];
}

export const openBuy = (symbol) => window.dispatchEvent(new CustomEvent("dmi:buy", { detail: { symbol } }));

export const BuyButton = ({ symbol, testid, label = "Buy", variant }) => (
  <Btn variant={variant} onClick={() => openBuy(symbol)} data-testid={testid || `buy-btn-${(symbol || "any").replace(/\W/g, "-").toLowerCase()}`}><ShoppingCart className="h-3.5 w-3.5" />{label}</Btn>
);

export const ManualBuy = ({ liveActive, initialSymbol }) => {
  useEffect(() => {
    if (!initialSymbol) return;
    window.history.replaceState(null, "", window.location.pathname);
    if (!liveActive) { toast.error(`Email link for ${initialSymbol}: activate LIVE first to buy`); return; }
    setTimeout(() => openBuy(initialSymbol), 300);
  }, [initialSymbol, liveActive]);
  if (!liveActive) return <span className="text-[11px] text-slate-600" data-testid="manual-buy-locked">Buy after LIVE is activated</span>;
  return <BuyButton testid="manual-buy-btn" />;
};

export const BuyDialog = () => {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [rows, load, halted, setRows] = useBuyable();
  const [f, setF] = useState({ symbol: "", order_type: "limit", qty: "", limit_price: "", stop: "", take_profit: "", mfa_code: "" });
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const row = rows?.find((r) => r.symbol === f.symbol);
  const dp = (v) => (v < 10 ? 3 : 2);
  const fill = (r, p) => setF((x) => ({ ...x, symbol: r.symbol, mfa_code: "",
    qty: String(p?.qty || r.lot),
    limit_price: r.price ? (r.price * 1.003).toFixed(dp(r.price)) : p?.entry ? p.entry.toFixed(dp(p.entry)) : "",
    stop: p?.stop ? p.stop.toFixed(dp(p.stop)) : r.price ? (r.price * 0.97).toFixed(dp(r.price)) : "",
    take_profit: p?.take_profit ? p.take_profit.toFixed(dp(p.take_profit)) : "" }));
  const loadPlan = async (sym) => {
    setPlan({ loading: true });
    try { const r = await api.get(`/live/buy-plan/${encodeURIComponent(sym)}`); setPlan(r.data.plan); return r.data; } catch (e) { setPlan(null); return null; }
  };
  const pick = async (sym) => {
    const r = rows.find((x) => x.symbol === sym);
    if (!r) { setF((x) => ({ ...x, symbol: "" })); setPlan(null); return; }
    fill(r); const d = await loadPlan(sym); if (d) fill(r, d.plan);
  };
  useEffect(() => {
    const on = async (e) => {
      const sym = e.detail?.symbol;
      setF({ symbol: "", order_type: "limit", qty: "", limit_price: "", stop: "", take_profit: "", mfa_code: "" });
      setRows(null);
      setOpen(true); setMissing(null); setPlan(null);
      const one = await load(sym);
      if (!sym) return;
      if (!one?.buyable) { setMissing(one ? `${sym}: ${one.reason || "not buyable now"}` : `${sym}: could not check`); return; }
      fill(one); const d = await loadPlan(sym); if (d) fill(one, d.plan);
    };
    window.addEventListener("dmi:buy", on);
    return () => window.removeEventListener("dmi:buy", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const price = f.order_type === "limit" ? parseFloat(f.limit_price) : row?.price;
  const qty = parseInt(f.qty, 10);
  const stop = parseFloat(f.stop);
  const tp = parseFloat(f.take_profit);
  const tpBad = f.take_profit !== "" && !(tp > price);
  const valid = !halted && row && qty > 0 && qty % row.lot === 0 && qty <= row.max_cash_buy && price > 0 && stop > 0 && stop < price && !tpBad;
  const submit = async () => {
    setBusy(true);
    try {
      const body = { symbol: f.symbol, order_type: f.order_type, qty, stop, limit_price: f.order_type === "limit" ? price : null, take_profit: f.take_profit ? parseFloat(f.take_profit) : null };
      const r = await api.post("/live/manual-buy", body);
      toast.success(`BUY ${qty} ${f.symbol} sent to Moomoo (${r.data.status || "submitted"})`); setOpen(false); setF((x) => ({ ...x, mfa_code: "" }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white" data-testid="manual-buy-dialog">
          <DialogHeader><DialogTitle>Manual BUY · REAL MONEY</DialogTitle>
            <DialogDescription>Only stocks your cash can buy now (last “Can Buy Now” check). Checks: cash + Emergency Stop. Stop-loss is required; DMI manages it for you. Empty take-profit = 2× your risk.</DialogDescription></DialogHeader>
          {halted && <div className="rounded-sm border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] text-red-700" data-testid="manual-buy-halted">Emergency Stop is active. Release it (top right) before buying.</div>}
          {missing && <div className="rounded-sm border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800" data-testid="manual-buy-missing">{missing}</div>}
          {plan?.loading && <div className="text-[11px] text-slate-500" data-testid="manual-buy-plan-loading">Loading the AI signal for this stock…</div>}
          {plan && !plan.loading && <div className={`rounded-sm border px-2 py-1.5 text-[11px] ${plan.status === "QUALIFIED" && plan.action === "BUY" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="manual-buy-plan">
            AI signal: <b>{plan.action || "—"} · {plan.status || "—"}</b>{plan.setup ? ` · ${plan.setup}` : ""}{plan.score != null ? ` · score ${plan.score}` : ""}. Price, stop-loss, take-profit{plan.qty ? " and quantity" : ""} filled from it{plan.qty_note ? ` (${plan.qty_note})` : ""}; you can change them.
            {!(plan.status === "QUALIFIED" && plan.action === "BUY") && plan.rejects?.length > 0 && <div className="mt-0.5">Not a qualified BUY now: {plan.rejects.join(" · ")}</div>}
          </div>}
          {rows === null ? <div className="text-xs text-slate-500">Loading…</div> : !rows.length ? <div className="text-xs text-amber-700" data-testid="manual-buy-empty">No buyable stocks. Run “Check now” on the Can Buy Now page first.</div> : (
            <div className="space-y-2">
              <Field label="Stock"><select data-testid="manual-buy-symbol" value={f.symbol} onChange={(e) => pick(e.target.value)} className={inp}>
                <option value="">Choose…</option>{rows.map((r) => <option key={r.symbol} value={r.symbol}>{r.symbol} · {r.name} · max {r.max_cash_buy} ({r.lots} lot){r.via ? " · RM collateral" : ""}</option>)}</select></Field>
              <div className="flex gap-1">{["limit", "market"].map((t) => <button key={t} data-testid={`manual-buy-type-${t}`} onClick={() => setF((x) => ({ ...x, order_type: t }))} className={`mono flex-1 rounded-sm border px-2 py-1 text-xs ${f.order_type === t ? "border-blue-500 bg-blue-600 text-white" : "border-[#E2E8F0] text-slate-600"}`}>{t.toUpperCase()}</button>)}</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label={`Quantity${row ? ` (lot ${row.lot})` : ""}`}><input data-testid="manual-buy-qty" inputMode="numeric" step={row?.lot || 1} value={f.qty} onChange={set("qty")} className={inp} /></Field>
                {f.order_type === "limit" ? <Field label="Limit price"><input data-testid="manual-buy-limit" inputMode="decimal" value={f.limit_price} onChange={set("limit_price")} className={inp} /></Field>
                  : <Field label="Market (approx.)"><div className={`${inp} text-slate-500`} data-testid="manual-buy-market-price">{row?.price ?? "—"}</div></Field>}
                <Field label="Stop-loss (required)"><input data-testid="manual-buy-stop" inputMode="decimal" value={f.stop} onChange={set("stop")} className={inp} /></Field>
                <Field label="Take-profit (optional)"><input data-testid="manual-buy-tp" inputMode="decimal" value={f.take_profit} onChange={set("take_profit")} className={inp} /></Field>
              </div>
              {row && price > 0 && qty > 0 && <div className="rounded-sm bg-[#F8FAFC] p-2 text-[11px] text-slate-700" data-testid="manual-buy-summary">
                Cost ≈ {fmt(qty * price, 2)} {row.currency}{stop > 0 && stop < price ? <> · max loss at stop ≈ {fmt(qty * (price - stop), 2)} {row.currency}</> : <span className="text-red-600"> · stop must be below price</span>}
                {tpBad && <div className="text-red-600">Take-profit must be above the buy price</div>}
                {qty > row.max_cash_buy && <div className="text-red-600">Above cash limit ({row.max_cash_buy})</div>}
                {qty % row.lot !== 0 && <div className="text-red-600">Must be a multiple of {row.lot}</div>}
                {f.order_type === "market" && <div className="text-amber-700">Market fills at the current price and may cost slightly more.</div>}
              </div>}
              <p className="text-[10px] text-emerald-700" data-testid="manual-buy-limit-note">Harga had asal = harga semasa +0.3% supaya order terus dibeli. Jika harga lari lebih dari itu, order menunggu (MENUNGGU).</p>
              <p className="text-[10px] text-slate-500" data-testid="manual-buy-example">Example: 9866.HK at HK$25.20, lot 10 → qty 20, limit 25.20, stop 24.40 → cost ≈ HK$504, max loss ≈ HK$16. Example US: AAPL qty 1, limit 230, stop 223.</p>
              <Btn disabled={busy || !valid} onClick={submit} data-testid="manual-buy-confirm">{busy ? "Sending…" : `Buy ${qty || 0} ${f.symbol || ""}`}</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
